/**
 * A observação de um item do orçamento ("2 verde e 2 amarelo").
 *
 * É texto livre, do item: acompanha a peça do orçamento ao pedido e à fila de produção, e sai no PDF do
 * orçamento (a observação GERAL do orçamento é que é interna). A regra de o que se grava e como se
 * preserva mora aqui, para o site e o app (rota do app) dizerem a mesma coisa.
 *
 * Três situações ao gravar um orçamento:
 *   - texto: vale o texto (aparado);
 *   - vazio ou `null`: a pessoa limpou a observação;
 *   - `undefined` (a tela ou o app antigo não conhece o campo): o que já estava gravado se mantém.
 */
import { z } from 'zod';

export const MAX_OBSERVACAO_DO_ITEM = 500;

/** Para o esquema do item: opcional, até 500 caracteres. */
export const ObservacaoDoItemSchema = z
  .string()
  .max(MAX_OBSERVACAO_DO_ITEM, `A observação do item tem no máximo ${MAX_OBSERVACAO_DO_ITEM} caracteres.`)
  .nullish();

/** O texto a gravar: aparado, sem linhas em branco sobrando nas pontas; vazio vira `null`. */
export function observacaoParaGravar(texto: string | null | undefined): string | null {
  const limpo = (texto ?? '').replace(/\r\n/g, '\n').trim();
  return limpo === '' ? null : limpo;
}

/**
 * Casa os itens novos com os de antes, para devolver a cada um a observação que tinha quando a tela
 * não mandou o campo (`undefined`). Quem mandou (texto, vazio ou `null`) fica com o que mandou.
 * O orçamento é regravado item a item (os ids mudam), então o par é pelo produto e a quantidade, e cada item de antes só serve uma vez (dois itens iguais não pegam a mesma observação).
 */
export function observacoesDeAntes<T extends { produto_id: string; quantidade: number; observacao?: string | null }>(
  anteriores: { produto_id: string; quantidade: number; observacao: string | null }[],
  novos: T[],
): (string | null | undefined)[] {
  const usados = new Set<number>();
  return novos.map((n) => {
    if (n.observacao !== undefined) return n.observacao; // a tela mandou: vale o que mandou
    const i = anteriores.findIndex((a, idx) => !usados.has(idx) && a.produto_id === n.produto_id && a.quantidade === n.quantidade);
    if (i < 0) return undefined;
    usados.add(i);
    return anteriores[i].observacao;
  });
}
